// Epoch 14: Campaign Creation System Types
// Based on database schema and 16-step workflow requirements

import { Campaign } from './Campaign';

export type ServiceTier = 'junior_sdr' | 'top_qualifier';
export type LeadSource = 'preset_filters' | 'csv_upload' | 'lead_generator' | 'past_list';
export type LeadSourceOrNull = LeadSource | null; // Allow null for uninitialized state
export type ExecutionStatus = 'not_started' | 'preparing' | 'running' | 'paused' | 'completed' | 'failed';

// Lead List Management
export interface LeadList {
  id: string;
  user_id: string;
  list_name: string;
  lead_source: LeadSource;
  lead_data: LeadData[];
  lead_count: number;
  source_metadata: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LeadData {
  id?: string;
  firstname?: string;
  lastname?: string;
  company?: string;
  position?: string;
  linkedin_url?: string;
  city?: string;
  industry?: string;
  topskill?: string;
  mutualconnectionname?: string;
  timeincurrentrole?: string;
  [key: string]: unknown;
}

// Message Sequences and Templates
export interface MessageContent {
  message_number: number;
  content: string;
  character_count: number;
  delay_days?: number;
}

export interface CampaignSequence {
  id: string;
  user_id: string;
  sequence_name: string;
  messages: MessageContent[];
  service_tier: ServiceTier;
  is_template: boolean;
  template_category?: string;
  template_description?: string;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface MessageTemplate {
  id: string;
  user_id: string;
  template_name: string;
  template_description?: string;
  messages: MessageContent[];
  service_tier: ServiceTier;
  category: string;
  tags: string[];
  usage_count: number;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

// Preset Filter System
export interface PresetLeadFilter {
  id: string;
  filter_name: string;
  filter_description?: string;
  filter_config: Record<string, unknown>;
  estimated_lead_count: number;
  is_active: boolean;
  display_order: number;
  created_by?: string;
  created_at: string;
  updated_at: string;
  last_used_at?: string;
  usage_count: number;
}

// Enhanced Campaign Interface (Epoch 14)
export interface CampaignV2 {
  // Core Campaign Data
  id: string;
  user_id: string;
  name: string;
  status: 'draft' | 'submitted' | 'preparing' | 'running' | 'completed';
  created_at: string;
  updated_at: string;
  
  // New Epoch 14 Fields
  sequence_id?: string;
  lead_lists_ids: string[];
  total_leads: number;
  service_tier: ServiceTier;
  ai_chatbot_config?: Record<string, unknown>;
  dashboard_url?: string;
  execution_config: Record<string, unknown>;
  execution_status: ExecutionStatus;
  submitted_at?: string;
  started_at?: string;
  completed_at?: string;
  
  // Legacy Fields (maintained for backward compatibility)
  assigned_csr?: string;
  target_audience_type?: 'csv_upload' | 'generic_filter';
  target_audience_filter?: Record<string, unknown>;
  target_audience_file_url?: string;
}

// Campaign Creation Wizard State
export interface CampaignCreationState {
  // Step 1-2: Basic Information
  campaign_name: string;
  service_tier: ServiceTier;
  
  // Step 3-6: Lead Acquisition
  lead_acquisition_method: LeadSourceOrNull; // Allow null to prevent race condition
  selected_preset_filters: string[];
  csv_file?: File;
  lead_generator_config?: Record<string, unknown>;
  selected_existing_lists: string[];
  
  // Step 7-14: Message Sequence
  sequence_name: string;
  messages: MessageContent[];
  use_existing_template: boolean;
  selected_template_id?: string;
  save_as_template: boolean;
  template_metadata?: {
    template_name: string;
    template_description: string;
    category: string;
    tags: string[];
  };
  
  // Step 15-16: Review and Confirmation
  total_estimated_leads: number;
  cost_estimate?: number;
  ai_chatbot_enabled: boolean; // Top Qualifier only
  ai_chatbot_config?: Record<string, unknown>; // Top Qualifier only
  submit_for_review?: boolean; // New field for campaign submission workflow
  
  // Workflow State
  current_step: number;
  validation_errors: Record<string, string[]>;
  is_loading: boolean;
  preview_data?: LeadData[];
  
  // Epoch 36 additions
  campaign_id?: string; // For edit mode
  preset_filter_data?: any; // Store filter data when no campaign_id exists yet
  filter_request_id?: string; // ID of created filter request
  csv_upload_id?: string; // ID of CSV upload
  lead_source_configured?: boolean; // Track if lead source is configured

  // Epoch 80 additions
  campaignGoal?: 'product_discovery' | 'lead_generation';
}

// Dynamic Variables for Message Templates
export const DYNAMIC_VARIABLES = [
  '%%first_name%%',
  '%%last_name%%', 
  '%%company%%',
  '%%position%%',
  '%%city%%',
  '%%industry%%',
  '%%topskill%%',
  '%%mutualconnectionname%%',
  '%%timeincurrentrole%%',
  '%%linkedinurl%%',
  '%%customvariable%%'
] as const;

export type DynamicVariable = typeof DYNAMIC_VARIABLES[number];

// Validation Rules
export interface ValidationRule {
  field: keyof CampaignCreationState;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: unknown) => string | null;
}

// Step Configuration
export interface WizardStep {
  step_number: number;
  title: string;
  description: string;
  component: string;
  validation_rules: ValidationRule[];
  is_optional: boolean;
  depends_on?: {
    field: keyof CampaignCreationState;
    value: unknown;
  };
}

// API Response Types
export interface CampaignCreationResponse {
  success: boolean;
  campaign_id?: string;
  message: string;
  errors?: Record<string, string[]>;
}

export interface LeadPreviewResponse {
  success: boolean;
  leads: LeadData[];
  total_count: number;
  estimated_cost?: number;
  message?: string;
}

export interface TemplateSearchResponse {
  success: boolean;
  templates: MessageTemplate[];
  total_count: number;
  message?: string;
}

// Component Props Types
export interface CampaignCreationWizardProps {
  onComplete?: (campaign: CampaignV2) => void;
  onCancel?: () => void;
  initialState?: Partial<CampaignCreationState>;
  serviceTier?: ServiceTier;
  existingCampaign?: Campaign; // Campaign data for edit/view mode
  isViewMode?: boolean;
  isEditMode?: boolean;
}

export interface WizardStepProps {
  state: CampaignCreationState;
  updateState: (updates: Partial<CampaignCreationState>) => void;
  onNext: () => void;
  onPrev: () => void;
  isLoading?: boolean;
  errors?: Record<string, string[]>;
  isViewMode?: boolean;
  isEditMode?: boolean;
  isFirstCampaign?: boolean;
}